import type { CascaderOption } from '@/components';

export type Recursive<T> = T & {
  children: Array<Recursive<T>> | null;
};

export const getRecursiveArray = <
  T extends { id: string; upperId: string | null },
>(
  data: T[],
  org: T[],
): Array<Recursive<T>> => {
  return data.flatMap((d) => {
    if (org.length === 0) {
      return [];
    }
    const children = org.filter((o) => o.upperId === d.id);
    if (children.length === 0) {
      return {
        ...d,
        children: null,
      };
    }
    return {
      ...d,
      children: getRecursiveArray(children, org),
    };
  });
};

export const findUpperRecursive = <
  T extends { id: string; upperId: string | null },
>(
  id: string,
  data: T[],
  current: T[] = [],
): T[] => {
  const target = data.find((d) => d.id === id);
  if (!target) {
    return current;
  }
  const upper = data.find((d) => d.id === target.upperId);
  if (!upper) {
    return [...current].concat(target);
  }

  return findUpperRecursive(upper.id, data, [...current].concat(target));
};

export const genRecursiveOptions = <
  T,
  R extends Recursive<T>,
  Value extends keyof R,
  Label extends keyof R,
>(
  data: R[],
  value: Value,
  label: Label,
): Array<Recursive<{ value: R[Value]; label: R[Label] }>> => {
  // @ts-expect-error
  return data.map((d) => {
    if (!d.children) {
      return {
        value: d[value],
        label: d[label],
        children: null,
      };
    }
    return {
      value: d[value],
      label: d[label],
      // @ts-expect-error
      children: genRecursiveOptions(d.children, value, label),
    };
  });
};

// TODO:
// BE will change upper as recursive but not completed yet,
// FE has to change the type from any to the certain one after then.
export const convertUpperToArray = <T extends object & { upper?: any }>(
  data: T,
  current: T[] = [],
): T[] => {
  if (!data.upper) {
    return [...current, data];
  }
  return convertUpperToArray(data.upper, [...current, data]);
};

export const getRecursiveIds = <T extends { id: string }>(
  list: Array<Recursive<T>>,
) => {
  const result = list.reduce<string[]>((acc, cur) => {
    const recursive = (data: Recursive<T>) => {
      if (!data.children?.length) {
        return;
      }

      acc.push(data.id);
      for (const child of data.children) {
        recursive(child);
      }
    };

    recursive(cur);

    return acc;
  }, []);

  return result;
};

export const transformToDataWithChildren = <T extends { descendant: T[] }>(
  data: T[],
): Array<Recursive<T>> => {
  return data.map((d) => ({
    ...d,
    children:
      d.descendant.length === 0
        ? null
        : transformToDataWithChildren(d.descendant),
  }));
};

export const filterRecursively = <T>(
  data: Array<Recursive<T>>,
  { key, value }: { key: string; value: any },
): Array<Recursive<T>> => {
  return data.flatMap((d) => {
    return key in d && d[key] === value
      ? d.children?.length
        ? { ...d, children: filterRecursively(d.children, { key, value }) }
        : d
      : [];
  });
};

export const extractIdsAndYearPeriod = <
  T extends { id: string; startYear: number | null; endYear: number | null },
>(
  arr: T[] | undefined,
) => ({
  ids: arr?.map((v) => v.id),
  startYear: Math.max(...(arr?.flatMap((v) => v.startYear ?? []) ?? [])),
  endYear: Math.min(...(arr?.flatMap((v) => v.endYear ?? []) ?? [])),
});

export const extractLevelAndRemoveChildren = <T extends { level: number }>(
  data: Array<Recursive<T>>,
  upperLevel: number | undefined,
): Array<Recursive<T>> => {
  if (!upperLevel) {
    return data;
  }
  return data.flatMap((recursive) => {
    if (recursive.level === upperLevel) {
      return { ...recursive, children: null };
    }
    if (!recursive.children) {
      return [];
    }
    return {
      ...recursive,
      children: extractLevelAndRemoveChildren(recursive.children, upperLevel),
    };
  });
};

export const getSelectedCascaderIds = (
  data: CascaderOption[],
  selectedIds: string[],
  parent: string[] | undefined = [],
) => {
  const result = data.reduce<string[][]>((acc, item) => {
    const data = [...parent, item.value.toString()];
    if (selectedIds.includes(item.value.toString())) {
      acc.push([...data]);
    } else if (item.children?.length) {
      acc.push(...getSelectedCascaderIds(item.children, selectedIds, data));
    }
    return acc;
  }, []);
  return result;
};

export const findChildrenRecursive = <T, K extends keyof T>(
  id: string,
  data: Recursive<T>[],
  key: K,
): string[] => {
  for (const d of data) {
    if (key in d) {
      const currentValue = d[key];
      if (typeof currentValue === 'string') {
        if (currentValue === id) {
          return [id];
        }
        if (d.children && d.children.length > 0) {
          const children = findChildrenRecursive(id, d.children, key);
          if (children.length > 0) {
            return [currentValue, ...children];
          }
        }
      }
    }
  }

  return [];
};

export const transformRecursiveDataWithUpper = (
  data: CascaderOption[],
  upperIds: string[] = [],
): Array<{ id: string; label: string; upperIdsPath: string[] }> => {
  const newData: Array<{ id: string; label: string; upperIdsPath: string[] }> =
    [];
  data.map((item) => {
    newData.push({
      id: item.value.toString(),
      label: item.label,
      upperIdsPath: upperIds,
    });
    if (item.children?.length) {
      newData.push(
        ...transformRecursiveDataWithUpper(item.children, [
          ...upperIds,
          item.value.toString(),
        ]),
      );
    }
  });
  return newData;
};

export const removeDuplicatesByKey = <T extends object, K extends keyof T>(
  array: T[],
  key: K,
): T[] => {
  return array.filter(
    (element, index, self) =>
      self.findIndex((e) => e[key] === element[key]) === index,
  );
};

export const extractLeafNodeIds = (data: CascaderOption[] = []): string[] => {
  return data.reduce<string[]>((acc, curr) => {
    if (curr.children?.length) {
      acc.push(...extractLeafNodeIds(curr.children));
    } else {
      acc.push(curr.value.toString());
    }
    return acc;
  }, []);
};
